import { Container, Typography } from '@cleartrip/ct-design-components'
import React, { useEffect, useRef, useState } from 'react'

import { DownChevron } from '@/assets/icons/DownChevron'
import { returnFareName } from '@/constants/components/fareType'
import { DEPARTURE_TIME_MAPPING_OBJ } from '@/constants/Srp'

import AppliedFiltersChip from './AppliedFiltersChip'
import VisaRejectionFilterLabel from './VisaRejectionFilterLabel'

const ptcChipLabel = (ptcFare?: { type?: string }, prefix = '') => {
  const fareName = returnFareName(ptcFare?.type || 'student') || 'Student'
  return `${prefix}${fareName} Benefits`
}

interface AppliedFiltersAllChipsProps {
  isRT: boolean
  isIntl: boolean
  isRevamp: boolean
  totalRoundTripPrice: any
  selectedFilters: any
  onFilterChange: any
  filterTypes: any
  isNonStopDefaultApplied?: boolean
  onNonStopTooltipDismiss?: () => void
}

const AppliedFiltersAllChips: React.FC<AppliedFiltersAllChipsProps> = ({
  isRT,
  isIntl,
  isRevamp,
  totalRoundTripPrice,
  selectedFilters,
  onFilterChange,
  filterTypes,
  isNonStopDefaultApplied = false,
  onNonStopTooltipDismiss,
}) => {
  const [showAllFilters, setShowAllFilters] = useState(false)
  const [filterOrder, setFilterOrder] = useState<string[]>([]) // Track order of filter changes
  const previousFilters = useRef<any>({})
  const [showNonStopTooltip, setShowNonStopTooltip] = useState(isNonStopDefaultApplied)
  
  // Show tooltip when non-stop default is applied
  useEffect(() => {
    if (isNonStopDefaultApplied) {
      setShowNonStopTooltip(true)
    }
  }, [isNonStopDefaultApplied])
  
  const handleNonStopTooltipDismiss = () => {
    setShowNonStopTooltip(false)
    onNonStopTooltipDismiss?.()
  }

  const {
    stops,
    airlineFilter,
    departureTimeFrom,
    arrivalTimeFrom,
    nearbyAirportFilter,
    tripDuration,
    layoverDuration,
    oneWayPrice,
    fareInclusions,
    layoverAirport,
    ptcFare: onwardPtcFare,
  } = selectedFilters?.onwardLeg || {}
  const {
    stops: returnStops,
    airlineFilter: returnAirlineFilter,
    departureTimeFrom: returnDepartureTimeFrom,
    arrivalTimeFrom: returnArrivalTimeFrom,
    nearbyAirportFilter: returnNearbyAirportFilter,
    tripDuration: returnTripDuration,
    layoverDuration: returnLayoverDuration,
    oneWayPrice: returnOneWayPrice,
    fareInclusions: returnFareInclusions,
    layoverAirport: returnLayoverAirport,
    ptcFare: returnPtcFare,
  } = selectedFilters?.returnLeg || {}
  const showCtUpgradeChip = selectedFilters?.ctUpgrade?.checked || false
  const showVisaRejectionChip = selectedFilters?.visaRejection?.checked || false

  // Track filter changes to maintain order
  useEffect(() => {
    const currentFilters = {
      totalRoundTripPrice: totalRoundTripPrice?.current,
      ctUpgrade: selectedFilters?.ctUpgrade?.checked,
      visaRejection: selectedFilters?.visaRejection?.checked,
      onwardPtcFare: onwardPtcFare?.checked,
      returnPtcFare: returnPtcFare?.checked,
      onwardCheckInBaggage: fareInclusions?.selectedValues,
      returnCheckInBaggage: returnFareInclusions?.selectedValues,
      onwardStops: stops?.selectedValues,
      returnStops: returnStops?.selectedValues,
      onwardAirlines: airlineFilter?.selectedValues,
      returnAirlines: returnAirlineFilter?.selectedValues,
      onwardPrice: oneWayPrice?.current,
      returnPrice: returnOneWayPrice?.current,
      onwardLayoverAirport: layoverAirport?.selectedOptions,
      returnLayoverAirport: returnLayoverAirport?.selectedOptions,
      onwardTripDuration: tripDuration?.current?.max,
      returnTripDuration: returnTripDuration?.current?.max,
      onwardLayoverDuration: layoverDuration?.current,
      returnLayoverDuration: returnLayoverDuration?.current,
      onwardDeparture: departureTimeFrom?.map((d: any) => d?.selectedValues),
      returnDeparture: returnDepartureTimeFrom?.map((d: any) => d?.selectedValues),
      onwardNearby: nearbyAirportFilter?.map((n: any) => n?.selectedValues),
      returnNearby: returnNearbyAirportFilter?.map((n: any) => n?.selectedValues),
    }

    const changedFilters: string[] = []

    Object.keys(currentFilters).forEach(key => {
      const currentValue = JSON.stringify(currentFilters[key as keyof typeof currentFilters])
      const previousValue = JSON.stringify(previousFilters.current[key])

      if (
        currentValue !== previousValue &&
        currentFilters[key as keyof typeof currentFilters] !== undefined &&
        currentFilters[key as keyof typeof currentFilters] !== null
      ) {
        // Handle array filters (nearby airports - track individual airports)
        if (key.includes('Nearby')) {
          const arrayValues = currentFilters[key as keyof typeof currentFilters] as any[]
          arrayValues?.forEach((value, index) => {
            if (value && value.length > 0) {
              value.forEach((airportValue: string, airportIndex: number) => {
                changedFilters.push(`${key}${index}_${airportIndex}`)
              })
            }
          })
        } else if (key.includes('Stops')) {
          // Handle individual stop values
          const stopValues = currentFilters[key as keyof typeof currentFilters] as number[]
          stopValues?.forEach((stopValue: number) => {
            changedFilters.push(`${key.replace('Stops', 'Stop')}${stopValue}`)
          })
        } else if (key.includes('Airlines')) {
          // Handle individual airline values
          const airlineValues = currentFilters[key as keyof typeof currentFilters] as string[]
          airlineValues?.forEach((airlineCode: string) => {
            changedFilters.push(`${key.replace('Airlines', 'Airline')}_${airlineCode}`)
          })
        } else if (key.includes('Departure') || key.includes('Arrival')) {
          // Handle individual time slot values for departure and arrival times
          const timeValues = currentFilters[key as keyof typeof currentFilters] as any[]
          timeValues?.forEach((value, index) => {
            if (value && value.length > 0) {
              value.forEach((timeSlot: string) => {
                changedFilters.push(`${key}${index}_${timeSlot}`)
              })
            }
          })
        } else if (key.includes('LayoverAirport')) {
          // Handle individual layover airport values
          const layoverAirportOptions = currentFilters[key as keyof typeof currentFilters] as any
          if (layoverAirportOptions && typeof layoverAirportOptions === 'object') {
            Object.entries(layoverAirportOptions).forEach(([country, airports]: [string, any]) => {
              if (Array.isArray(airports)) {
                airports.forEach((airportName: string) => {
                  changedFilters.push(`${key}_${country}_${airportName}`)
                })
              }
            })
          }
        } else {
          changedFilters.push(key)
        }
      }
    })

    if (changedFilters.length > 0) {
      setFilterOrder(prev => {
        // Remove existing instances and add changed filters to the end (most recent)
        const filteredPrev = prev.filter(f => !changedFilters.includes(f))
        const newOrder = [...filteredPrev, ...changedFilters]
        return newOrder
      })
    }

    previousFilters.current = currentFilters
  }, [
    totalRoundTripPrice,
    selectedFilters,
    selectedFilters?.ctUpgrade,
    selectedFilters?.visaRejection,
    stops,
    returnStops,
    airlineFilter,
    returnAirlineFilter,
    oneWayPrice,
    returnOneWayPrice,
    layoverAirport,
    returnLayoverAirport,
    tripDuration,
    returnTripDuration,
    layoverDuration,
    returnLayoverDuration,
    departureTimeFrom,
    returnDepartureTimeFrom,
    nearbyAirportFilter,
    returnNearbyAirportFilter,
    onwardPtcFare,
    returnPtcFare,
  ])

  const cleanAirportName = (airportName: string): string => {
    return (airportName || '').replace(/\s{0,20}\([^)]{0,200}\)\s{0,20}/g, '').trim()
  }

  // Helper function to get all active filters for chips
  const getActiveFilters = () => {
    const activeFilters: Array<{
      label: any
      onRemove: () => void
      key: string
      orderKey?: string
      logoLink?: string
    }> = []

    // Visa Rejection Cover - Show at the top when applied
    if (showVisaRejectionChip) {
      activeFilters.push({
        key: 'visaRejection',
        label: <VisaRejectionFilterLabel variant='B3' />,
        onRemove: () => onFilterChange(filterTypes.VISA_REJECTION, { ...selectedFilters.visaRejection, checked: false }),
        orderKey: 'visaRejection',
      })
    }

    // Total Round Trip Price
    if (isRT && (isIntl || isRevamp) && totalRoundTripPrice?.current && totalRoundTripPrice.current < totalRoundTripPrice.max) {
      activeFilters.push({
        key: 'totalRoundTripPrice',
        label: `Total: ₹${totalRoundTripPrice.current?.toLocaleString('en-IN')}`,
        onRemove: () => onFilterChange(filterTypes.TOTAL_ROUND_TRIP_PRICE, totalRoundTripPrice.max),
        orderKey: 'totalRoundTripPrice',
      })
    }

    // CT Upgrade
    if (showCtUpgradeChip) {
      activeFilters.push({
        key: 'ctUpgrade',
        label: 'CT Upgrade',
        onRemove: () => onFilterChange(filterTypes.CT_UPGRADE, { ...selectedFilters.ctUpgrade, checked: false }),
        orderKey: 'ctUpgrade',
      })
    }

    if (onwardPtcFare?.checked) {
      activeFilters.push({
        key: 'onwardPtcFare',
        label: ptcChipLabel(onwardPtcFare, isRT ? 'Onward: ' : ''),
        onRemove: () => onFilterChange(filterTypes.PTC_FARE, { ...onwardPtcFare, checked: false }, 0, 'onwardLeg'),
        orderKey: 'onwardPtcFare',
      })
    }
    if (returnPtcFare?.checked) {
      activeFilters.push({
        key: 'returnPtcFare',
        label: ptcChipLabel(returnPtcFare, 'Return: '),
        onRemove: () => onFilterChange(filterTypes.PTC_FARE, { ...returnPtcFare, checked: false }, 1, 'returnLeg'),
        orderKey: 'returnPtcFare',
      })
    }

    // Check In Baggage
    if (fareInclusions?.selectedValues?.includes('withBaggage')) {
      activeFilters.push({
        key: 'checkInBaggage',
        label: isRT ? 'Onward: Include check-in' : 'Include check-in',
        onRemove: () => onFilterChange(filterTypes.CHECK_IN_BAGGAGE, [], 0, 'onwardLeg'),
        orderKey: 'onwardCheckInBaggage',
      })
    }
    if (isRT && returnFareInclusions?.selectedValues?.includes('withBaggage')) {
      activeFilters.push({
        key: 'returnCheckInBaggage',
        label: 'Return: Include check-in',
        onRemove: () => onFilterChange(filterTypes.CHECK_IN_BAGGAGE, [], 0, 'returnLeg'),
        orderKey: 'returnCheckInBaggage',
      })
    }

    // Stops - Onward (Individual chips for each stop type)
    if (stops?.selectedValues?.length > 0) {
      stops.selectedValues.forEach((stop: number) => {
        const stopLabel = stop === 0 ? 'Non-stop' : stop === 2 ? '2+ Stops' : `${stop} Stop`
        activeFilters.push({
          key: `onwardStop${stop}`,
          label: isRT ? `Onward: ${stopLabel}` : stopLabel,
          onRemove: () => {
            // Remove only this specific stop value
            const updatedStops = stops.selectedValues.filter((s: number) => s !== stop)
            onFilterChange(filterTypes.STOPS, updatedStops, 0, 'onwardLeg')
          },
          orderKey: `onwardStop${stop}`,
        })
      })
    }

    // Stops - Return (Individual chips for each stop type)
    if (isRT && returnStops?.selectedValues?.length > 0) {
      returnStops.selectedValues.forEach((stop: number) => {
        const stopLabel = stop === 0 ? 'Non-stop' : stop === 2 ? '2+ Stops' : `${stop} Stop`
        activeFilters.push({
          key: `returnStop${stop}`,
          label: `Return: ${stopLabel}`,
          onRemove: () => {
            // Remove only this specific stop value
            const updatedStops = returnStops.selectedValues.filter((s: number) => s !== stop)
            onFilterChange(filterTypes.STOPS, updatedStops, 0, 'returnLeg')
          },
          orderKey: `returnStop${stop}`,
        })
      })
    }

    // Airlines - Onward (Individual chips for each airline)
    if (airlineFilter?.selectedValues?.length > 0) {
      airlineFilter.selectedValues.forEach((airlineCode: string) => {
        // Find the corresponding airline name from options
        const airlineOption = airlineFilter.options?.find((option: any) => option.value === airlineCode)
        const airlineName = airlineOption?.label || airlineCode

        activeFilters.push({
          key: `onwardAirline_${airlineCode}`,
          label: isRT ? `Onward: ${airlineName}` : airlineName,
          onRemove: () => {
            // Remove only this specific airline
            const updatedAirlines = airlineFilter.selectedValues.filter((code: string) => code !== airlineCode)
            onFilterChange(filterTypes.AIRLINE_FILTER, updatedAirlines, 0, 'onwardLeg')
          },
          orderKey: `onwardAirline_${airlineCode}`,
        })
      })
    }

    // Airlines - Return (Individual chips for each airline)
    if (isRT && returnAirlineFilter?.selectedValues?.length > 0) {
      returnAirlineFilter.selectedValues.forEach((airlineCode: string) => {
        // Find the corresponding airline name from options
        const airlineOption = returnAirlineFilter.options?.find((option: any) => option.value === airlineCode)
        const airlineName = airlineOption?.label || airlineCode

        activeFilters.push({
          key: `returnAirline_${airlineCode}`,
          label: `Return: ${airlineName}`,
          onRemove: () => {
            // Remove only this specific airline
            const updatedAirlines = returnAirlineFilter.selectedValues.filter((code: string) => code !== airlineCode)
            onFilterChange(filterTypes.AIRLINE_FILTER, updatedAirlines, 0, 'returnLeg')
          },
          orderKey: `returnAirline_${airlineCode}`,
        })
      })
    }

    // Departure Time - Onward (Individual chips for each time slot)
    departureTimeFrom?.forEach((departure: any, index: number) => {
      if (departure?.selectedValues?.length > 0) {
        departure.selectedValues.forEach((timeSlot: string) => {
          // Get the time slot label from mapping object
          const timeSlotInfo = DEPARTURE_TIME_MAPPING_OBJ[timeSlot as keyof typeof DEPARTURE_TIME_MAPPING_OBJ]
          const timeLabel = timeSlotInfo ? timeSlotInfo.subLabel : timeSlot

          activeFilters.push({
            key: `onwardDeparture${index}_${timeSlot}`,
            label: isRT ? `Onward: ${timeLabel}` : timeLabel,
            onRemove: () => {
              // Remove only this specific time slot
              const updatedTimeSlots = departure.selectedValues.filter((slot: string) => slot !== timeSlot)
              onFilterChange(filterTypes.DEPARTURE_TIME_FROM, updatedTimeSlots, index, 'onwardLeg')
            },
            orderKey: `onwardDeparture${index}_${timeSlot}`,
            logoLink: 'https://static-assets-ct.flixcart.com/ct/assets/resources/images/flight_takeoff.png',
          })
        })
      }
    })

    // Departure Time - Return (Individual chips for each time slot)
    returnDepartureTimeFrom?.forEach((departure: any, index: number) => {
      if (departure?.selectedValues?.length > 0) {
        departure.selectedValues.forEach((timeSlot: string) => {
          // Get the time slot label from mapping object
          const timeSlotInfo = DEPARTURE_TIME_MAPPING_OBJ[timeSlot as keyof typeof DEPARTURE_TIME_MAPPING_OBJ]
          const timeLabel = timeSlotInfo ? timeSlotInfo.subLabel : timeSlot

          activeFilters.push({
            key: `returnDeparture${index}_${timeSlot}`,
            label: `Return: ${timeLabel}`,
            onRemove: () => {
              // Remove only this specific time slot
              const updatedTimeSlots = departure.selectedValues.filter((slot: string) => slot !== timeSlot)
              onFilterChange(filterTypes.DEPARTURE_TIME_FROM, updatedTimeSlots, index, 'returnLeg')
            },
            orderKey: `returnDeparture${index}_${timeSlot}`,
            logoLink: 'https://static-assets-ct.flixcart.com/ct/assets/resources/images/flight_takeoff.png',
          })
        })
      }
    })

    // Arrival Time - Onward (Individual chips for each time slot)
    arrivalTimeFrom?.forEach((arrival: any, index: number) => {
      if (arrival?.selectedValues?.length > 0) {
        arrival.selectedValues.forEach((timeSlot: string) => {
          // Get the time slot label from mapping object
          const timeSlotInfo = DEPARTURE_TIME_MAPPING_OBJ[timeSlot as keyof typeof DEPARTURE_TIME_MAPPING_OBJ]
          const timeLabel = timeSlotInfo ? timeSlotInfo.subLabel : timeSlot

          activeFilters.push({
            key: `onwardArrival${index}_${timeSlot}`,
            label: isRT ? `Onward: ${timeLabel}` : timeLabel,
            onRemove: () => {
              // Remove only this specific time slot
              const updatedTimeSlots = arrival.selectedValues.filter((slot: string) => slot !== timeSlot)
              onFilterChange(filterTypes.ARRIVAL_TIME_FROM, updatedTimeSlots, index, 'onwardLeg')
            },
            orderKey: `onwardArrival${index}_${timeSlot}`,
            logoLink: 'https://static-assets-ct.flixcart.com/ct/assets/resources/images/flight_land%20(1).png',
          })
        })
      }
    })

    // Arrival Time - Return (Individual chips for each time slot)
    returnArrivalTimeFrom?.forEach((arrival: any, index: number) => {
      if (arrival?.selectedValues?.length > 0) {
        arrival.selectedValues.forEach((timeSlot: string) => {
          // Get the time slot label from mapping object
          const timeSlotInfo = DEPARTURE_TIME_MAPPING_OBJ[timeSlot as keyof typeof DEPARTURE_TIME_MAPPING_OBJ]
          const timeLabel = timeSlotInfo ? timeSlotInfo.subLabel : timeSlot

          activeFilters.push({
            key: `returnArrival${index}_${timeSlot}`,
            label: `Return: ${timeLabel}`,
            onRemove: () => {
              // Remove only this specific time slot
              const updatedTimeSlots = arrival.selectedValues.filter((slot: string) => slot !== timeSlot)
              onFilterChange(filterTypes.ARRIVAL_TIME_FROM, updatedTimeSlots, index, 'returnLeg')
            },
            orderKey: `returnArrival${index}_${timeSlot}`,
            logoLink: 'https://static-assets-ct.flixcart.com/ct/assets/resources/images/flight_land%20(1).png',
          })
        })
      }
    })

    // One Way Price - Onward
    if (oneWayPrice?.current && oneWayPrice.current < oneWayPrice.max) {
      activeFilters.push({
        key: 'onwardPrice',
        label: isRT
          ? `Onward: Under ₹${oneWayPrice.current?.toLocaleString('en-IN')}`
          : `Under ₹${oneWayPrice.current?.toLocaleString('en-IN')}`,
        onRemove: () => onFilterChange(filterTypes.ONE_WAY_PRICE, oneWayPrice.max, 0, 'onwardLeg'),
        orderKey: 'onwardPrice',
      })
    }

    // One Way Price - Return
    if (isRT && returnOneWayPrice?.current && returnOneWayPrice.current < returnOneWayPrice.max) {
      activeFilters.push({
        key: 'returnPrice',
        label: `Return: Under ₹${returnOneWayPrice.current?.toLocaleString('en-IN')}`,
        onRemove: () => onFilterChange(filterTypes.ONE_WAY_PRICE, returnOneWayPrice.max, 0, 'returnLeg'),
        orderKey: 'returnPrice',
      })
    }

    // Layover Airport - Onward (Individual chips for each airport)
    if (layoverAirport?.selectedOptions && Object.keys(layoverAirport.selectedOptions).length > 0) {
      Object.entries(layoverAirport.selectedOptions).forEach(([country, airports]: [string, any]) => {
        if (Array.isArray(airports)) {
          airports.forEach((airportName: string) => {
            const cleanedAirportName = cleanAirportName(airportName)
            activeFilters.push({
              key: `onwardLayoverAirport_${country}_${airportName}`,
              label: isRT ? `Onward: ${cleanedAirportName}` : cleanedAirportName,
              onRemove: () => {
                // Create flat array of all selected airports except this one
                const allSelectedAirports: string[] = []
                Object.entries(layoverAirport.selectedOptions).forEach(([c, aplist]: [string, any]) => {
                  if (Array.isArray(aplist)) {
                    aplist.forEach((ap: string) => {
                      if (!(c === country && ap === airportName)) {
                        allSelectedAirports.push(ap)
                      }
                    })
                  }
                })
                onFilterChange(filterTypes.LAYOVER_AIRPORT, allSelectedAirports, 0, 'onwardLeg')
              },
              orderKey: `onwardLayoverAirport_${country}_${airportName}`,
            })
          })
        }
      })
    }

    // Layover Airport - Return (Individual chips for each airport)
    if (isRT && returnLayoverAirport?.selectedOptions && Object.keys(returnLayoverAirport.selectedOptions).length > 0) {
      Object.entries(returnLayoverAirport.selectedOptions).forEach(([country, airports]: [string, any]) => {
        if (Array.isArray(airports)) {
          airports.forEach((airportName: string) => {
            const cleanedAirportName = cleanAirportName(airportName)
            activeFilters.push({
              key: `returnLayoverAirport_${country}_${airportName}`,
              label: `Return: ${cleanedAirportName}`,
              onRemove: () => {
                // Create flat array of all selected airports except this one
                const allSelectedAirports: string[] = []
                Object.entries(returnLayoverAirport.selectedOptions).forEach(([c, aplist]: [string, any]) => {
                  if (Array.isArray(aplist)) {
                    aplist.forEach((ap: string) => {
                      if (!(c === country && ap === airportName)) {
                        allSelectedAirports.push(ap)
                      }
                    })
                  }
                })
                onFilterChange(filterTypes.LAYOVER_AIRPORT, allSelectedAirports, 0, 'returnLeg')
              },
              orderKey: `returnLayoverAirport_${country}_${airportName}`,
            })
          })
        }
      })
    }

    // Nearby Airports - Onward
    nearbyAirportFilter?.forEach((nearby: any, index: number) => {
      if (nearby?.selectedValues?.length > 0) {
        nearby.selectedValues.forEach((selectedValue: string, airportIndex: number) => {
          // Find the corresponding airport name from options
          const airportOption = nearby.options?.find((option: any) => option.value === selectedValue)
          const airportName = airportOption?.label || selectedValue
          const cleanedAirportName = cleanAirportName(airportName)

          activeFilters.push({
            key: `onwardNearby${index}_${airportIndex}`,
            label: isRT ? `Onward: ${cleanedAirportName}` : cleanedAirportName,
            onRemove: () => {
              // Remove only this specific airport
              const updatedAirports = nearby.selectedValues.filter((_: any, i: number) => i !== airportIndex)
              onFilterChange(filterTypes.NEARBY_AIRPORT, updatedAirports, index, 'onwardLeg')
            },
            orderKey: `onwardNearby${index}_${airportIndex}`,
          })
        })
      }
    })

    // Nearby Airports - Return
    returnNearbyAirportFilter?.forEach((nearby: any, index: number) => {
      if (nearby?.selectedValues?.length > 0) {
        nearby.selectedValues.forEach((selectedValue: string, airportIndex: number) => {
          // Find the corresponding airport name from options
          const airportOption = nearby.options?.find((option: any) => option.value === selectedValue)
          const airportName = airportOption?.label || selectedValue
          const cleanedAirportName = cleanAirportName(airportName)

          activeFilters.push({
            key: `returnNearby${index}_${airportIndex}`,
            label: `Return: ${cleanedAirportName}`,
            onRemove: () => {
              // Remove only this specific airport
              const updatedAirports = nearby.selectedValues.filter((_: any, i: number) => i !== airportIndex)
              onFilterChange(filterTypes.NEARBY_AIRPORT, updatedAirports, index, 'returnLeg')
            },
            orderKey: `returnNearby${index}_${airportIndex}`,
          })
        })
      }
    })

    // Trip Duration - Onward
    if (tripDuration?.current?.max && tripDuration.current.max < tripDuration.max) {
      activeFilters.push({
        key: 'onwardTripDuration',
        label: isRT ? `Onward: ${tripDuration.current.max}h` : `${tripDuration.current.max}h`,
        onRemove: () => onFilterChange(filterTypes.TRIP_DURATION, tripDuration.max, 0, 'onwardLeg'),
        orderKey: 'onwardTripDuration',
        logoLink: 'https://static-assets-ct.flixcart.com/ct/assets/resources/images/schedule.png',
      })
    }

    // Trip Duration - Return
    if (isRT && returnTripDuration?.current?.max && returnTripDuration.current.max < returnTripDuration.max) {
      activeFilters.push({
        key: 'returnTripDuration',
        label: `Return: ${returnTripDuration.current.max}h`,
        onRemove: () => onFilterChange(filterTypes.TRIP_DURATION, returnTripDuration.max, 0, 'returnLeg'),
        orderKey: 'returnTripDuration',
        logoLink: 'https://static-assets-ct.flixcart.com/ct/assets/resources/images/schedule.png',
      })
    }

    // Layover Duration - Onward
    if (
      layoverDuration?.current &&
      (layoverDuration.current.min !== layoverDuration.min || layoverDuration.current.max !== layoverDuration.max)
    ) {
      const { min, max } = layoverDuration.current
      activeFilters.push({
        key: 'onwardLayoverDuration',
        label: isRT ? `Onward stopover: ${min}h - ${max}h` : `Stopover: ${min}h - ${max}h`,
        onRemove: () =>
          onFilterChange(
            filterTypes.LAYOVER_DURATION,
            { min: layoverDuration.min, max: layoverDuration.max },
            0,
            'onwardLeg'
          ),
        orderKey: 'onwardLayoverDuration',
      })
    }

    // Layover Duration - Return
    if (
      isRT &&
      returnLayoverDuration?.current &&
      (returnLayoverDuration.current.min !== returnLayoverDuration.min ||
        returnLayoverDuration.current.max !== returnLayoverDuration.max)
    ) {
      const { min, max } = returnLayoverDuration.current
      activeFilters.push({
        key: 'returnLayoverDuration',
        label: `Return stopover: ${min}h - ${max}h`,
        onRemove: () =>
          onFilterChange(
            filterTypes.LAYOVER_DURATION,
            { min: returnLayoverDuration.min, max: returnLayoverDuration.max },
            0,
            'returnLeg'
          ),
        orderKey: 'returnLayoverDuration',
      })
    }
    // Sort active filters based on most recent changes - latest first
    // Visa Rejection Cover should always appear at the top when applied
    const sortedFilters = activeFilters.sort((a, b) => {
      // Always prioritize visa rejection chip at the top
      if (a.key === 'visaRejection') return -1
      if (b.key === 'visaRejection') return 1

      const aOrderKey = a.orderKey || a.key
      const bOrderKey = b.orderKey || b.key
      const aIndex = filterOrder.indexOf(aOrderKey)
      const bIndex = filterOrder.indexOf(bOrderKey)

      // If both filters are tracked, sort by reverse order (most recent first)
      if (aIndex !== -1 && bIndex !== -1) {
        return bIndex - aIndex
      }
      // If only one is tracked, prioritize it (put it first)
      if (aIndex !== -1) return -1
      if (bIndex !== -1) return 1
      // If neither is tracked, maintain original order
      return 0
    })

    return sortedFilters
  }

  const activeFilters = getActiveFilters()
  const shouldShowViewAll = activeFilters.length > 10
  const displayedFilters = shouldShowViewAll && !showAllFilters ? activeFilters.slice(0, 10) : activeFilters
  const chipsRowClassName = isRevamp ? 'flex flex-wrap pt-1 pb-2' : 'flex flex-wrap pt-4 pb-2'

  return (
    <>
      {activeFilters.length > 0 && (
        <Container className={chipsRowClassName} style={{ gap: '8px' }}>
          {displayedFilters.map(filter => {
            // Show tooltip on the first non-stop chip (onwardStop0) when non-stop default is applied
            const shouldShowTooltip = showNonStopTooltip && filter.key === 'onwardStop0'
            return (
              <AppliedFiltersChip
                key={filter.key}
                label={filter.label}
                logoLink={filter.logoLink}
                showNonStopTooltip={shouldShowTooltip}
                onRemove={filter.onRemove}
                onTooltipDismiss={handleNonStopTooltipDismiss}
              />
            )
          })}

          {shouldShowViewAll && (
            <Container
              className='flex flex-between flex-middle c-pointer mt-3 w-100p'
              onClick={() => setShowAllFilters(!showAllFilters)}
            >
              <Typography color='link' variant='L3'>
                {showAllFilters ? 'Hide' : 'View all applied filters'}
              </Typography>
              <DownChevron
                className='mr-1'
                height='16'
                style={{
                  transition: 'transform 0.2s ease-in-out',
                  transform: showAllFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
                width='16'
              />
            </Container>
          )}
        </Container>
      )}
    </>
  )
}

export default AppliedFiltersAllChips
